import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { Languages, Lock, Eye, EyeOff, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) return setError(t("authPasswordTooShort"));
    if (password !== confirm) return setError(t("authPasswordsMismatch"));
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: password }),
      });
      if (res.ok) {
        setSuccess(true);
        setTimeout(() => navigate("/auth"), 3000);
      } else {
        setError(t("authResetInvalidToken"));
      }
    } catch {
      setError(t("authGenericError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-2">
            <Languages className="w-10 h-10 text-white" />
            <h1 className="text-2xl font-bold text-white">{t("appTitle")}</h1>
          </div>
        </div>

        <Card className="bg-zinc-900 border-zinc-800" data-testid="reset-password-card">
          <CardHeader className="text-center">
            <CardTitle className="text-white text-xl">{t("authResetTitle")}</CardTitle>
            <CardDescription className="text-zinc-400">{t("authResetDesc")}</CardDescription>
          </CardHeader>
          <CardContent>
            {!token ? (
              <p className="text-red-400 text-sm text-center" data-testid="reset-password-no-token">{t("authResetInvalidToken")}</p>
            ) : success ? (
              <div className="text-center space-y-3" data-testid="reset-password-success">
                <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto" />
                <p className="text-zinc-200">{t("authResetSuccess")}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password" className="text-zinc-300">{t("authNewPassword")}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      id="new-password"
                      data-testid="reset-password-new-input"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      className="pl-10 pr-10 bg-zinc-800 border-zinc-700 text-white"
                    />
                    <button
                      type="button"
                      data-testid="reset-password-toggle-visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password" className="text-zinc-300">{t("authConfirmPassword")}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      id="confirm-password"
                      data-testid="reset-password-confirm-input"
                      type={showPassword ? "text" : "password"}
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      required
                      minLength={6}
                      className="pl-10 bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                </div>
                {error && <p className="text-red-400 text-sm" data-testid="reset-password-error">{error}</p>}
                <Button type="submit" disabled={loading} data-testid="reset-password-submit" className="w-full bg-white text-black hover:bg-zinc-200">
                  {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {t("authResetButton")}
                </Button>
              </form>
            )}
            <div className="mt-6 text-center">
              <Link to="/auth" data-testid="reset-password-back-login" className="text-zinc-400 hover:text-white text-sm">
                {t("authBackToLogin")}
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
